/**
 * Compact Conversation Simulator component
 *
 * This component provides a more focused simulation experience specifically
 * built around the improved LiveGuidance architecture. It automatically
 * generates personalized greeting messages and simulates conversations.
 */
import { useState } from "react";

import { useServerUrl } from "~/app/hooks/useServerUrl";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import { sampleDialogue, useScriptStore } from "./ScriptStore";
import type {
  ConversationConfig,
  TranscriptEvent,
} from "./core/models/ScriptItemTypes";
import { MockBANTStrategy } from "./core/strategies/implementation/MockBANTStrategy";

// Define a type for the botSocket that matches what's passed from CallDisplay
type BotSocketType = {
  send: (message: string) => void;
  close: () => void;
  reconnect: () => void;
};

interface CompactConversationSimulatorProps {
  botSocket: BotSocketType;
}

interface CustomerProfile {
  name: string;
  company: string;
  role: string;
  problem: string;
  budget: string;
  timeline: string;
  interests: string;
}

/**
 * CompactConversationSimulator provides a simplified interface for
 * testing the LiveGuidance feature with automatic greeting generation
 */
export function CompactConversationSimulator({
  botSocket,
}: Readonly<CompactConversationSimulatorProps>) {
  const serverUrl = useServerUrl();
  const [isLoading, setIsLoading] = useState(false);
  const [persona, setPersona] = useState<
    "executive" | "technical" | "skeptical" | "enthusiastic"
  >("executive");
  const [transcriptIndex, setTranscriptIndex] = useState(0);
  const [customerResponse, setCustomerResponse] = useState("");
  const [conversationHistory, setConversationHistory] = useState<
    Array<{ speaker: string; text: string }>
  >([]);
  const [sdrName, setSdrName] = useState("Stef");
  const [companyName, setCompanyName] = useState("Frontier AI");

  // Auto-greeting simulation support
  const [useAutoGreeting, setUseAutoGreeting] = useState(true);
  const [simulationRunning, setSimulationRunning] = useState(false);
  const [simulationStep, setSimulationStep] = useState(0);
  const maxSimulationSteps = 10;

  // Store actions
  const {
    addTranscriptEvent,
    currentItem,
    completeScriptItem,
    insertScriptItem,
    reset,
  } = useScriptStore();

  // Initialize BANT strategy for enhanced simulation
  const bantStrategy = new MockBANTStrategy(false);

  // Predefined customer personas
  const personas: Record<
    "executive" | "technical" | "skeptical" | "enthusiastic",
    CustomerProfile
  > = {
    executive: {
      name: "Alex Johnson",
      company: "TechInnovate",
      role: "Director of Sales",
      problem: "Sales team spending too much time on administrative tasks",
      budget: "$50,000 per year",
      timeline: "Needs a solution in the next 3 months",
      interests: "Improving sales efficiency, automation, and ROI metrics",
    },
    technical: {
      name: "Sam Chen",
      company: "DataTech Systems",
      role: "CTO",
      problem: "Integration with existing systems and scalability concerns",
      budget: "$75,000 for implementation",
      timeline: "Looking to implement before end of fiscal year",
      interests:
        "Technical specifications, API documentation, security features",
    },
    skeptical: {
      name: "Jamie Williams",
      company: "Consolidated Services",
      role: "Procurement Manager",
      problem: "Previous vendor solutions failed to deliver promised results",
      budget: "Limited - needs strong ROI justification",
      timeline: "No urgent timeline, cautious approach",
      interests: "Proof points, case studies, guarantees, and references",
    },
    enthusiastic: {
      name: "Taylor Rodriguez",
      company: "GrowthStart",
      role: "CEO",
      problem: "Need to scale quickly with limited resources",
      budget: "Flexible for the right solution",
      timeline: "ASAP - experiencing rapid growth",
      interests:
        "Quick implementation, future roadmap, partnership opportunities",
    },
  };

  // Get customer response from LLM
  const getCustomerResponse = async (persona: string, conversation: string) => {
    setIsLoading(true);
    try {
      const selectedPersona = personas[persona as keyof typeof personas];

      // Prepare the prompt with conversation history
      const prompt = `
You are roleplaying as a customer named ${selectedPersona.name} who works at ${
        selectedPersona.company
      } as a ${selectedPersona.role}.

Problem: ${selectedPersona.problem}
Budget: ${selectedPersona.budget}
Timeline: ${selectedPersona.timeline}
Interests: ${selectedPersona.interests}

You are on a call with a sales representative. Respond naturally as if you're the customer. Keep your response brief (1-2 sentences). You are ${
        persona === "skeptical"
          ? "cautious and need convincing"
          : persona === "enthusiastic"
            ? "excited and eager to learn more"
            : "professional and focused on business value"
      }.

Here's the conversation so far:
${conversation}

Respond as the customer:`;

      // Use local mock strategy to generate response if enabled
      if (useAutoGreeting) {
        // Convert the conversationHistory to transcript events for the strategy
        const events: TranscriptEvent[] = conversationHistory.map(
          (item, index) => ({
            id: index.toString(),
            timestamp: Date.now() - index * 1000,
            speaker: item.speaker === "sdr" ? "sdr" : "customer",
            text: item.text,
          }),
        );

        // Generate response using the strategy
        return await bantStrategy.simulateCustomerResponse(
          events[events.length - 1]?.text ||
            "Hello, I'm interested in learning more.",
          events,
        );
      }

      // Fall back to API call if auto-greeting is disabled
      const response = await fetch(
        `${serverUrl}/parties/feedback/test/api/roleplay/customer`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            prompt,
            maxTokens: 150,
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      const data = await response.json();
      return data.response.trim();
    } catch (error) {
      console.error("Failed to get customer response:", error);
      return "I'm sorry, I didn't quite catch that. Could you explain your product a bit more?";
    } finally {
      setIsLoading(false);
    }
  };

  // Generate greeting using the enhanced LiveGuidance architecture
  const generateAutomaticGreeting = async () => {
    if (!useAutoGreeting) {
      // Fall back to original implementation if auto-greeting is disabled
      return null;
    }

    try {
      // Create conversation config
      const config: ConversationConfig = {
        sdr: {
          name: sdrName,
          role: "SDR",
          company: companyName,
        },
        customer: {
          name: personas[persona as keyof typeof personas].name,
          role: personas[persona as keyof typeof personas].role,
          company: personas[persona as keyof typeof personas].company,
        },
        greetingTemplate: `Hello, I'm {name} from {company}. I'm reaching out today to learn more about your current challenges and see if we might be able to help. Could you tell me a bit about what brought you to this call?`,
        strategyType: "bant",
      };

      // Generate welcome message using strategy
      const welcomeResult = await bantStrategy.generateWelcomeMessage(config);

      if (
        welcomeResult.shouldGenerateScriptItem &&
        welcomeResult.suggestedScript?.text
      ) {
        return welcomeResult.suggestedScript.text;
      }

      return null;
    } catch (error) {
      console.error("Error generating automatic greeting:", error);
      return null;
    }
  };

  // Send initial greeting with host name and company to trigger the realtime API
  const triggerInitialGreeting = async () => {
    setIsLoading(true);

    try {
      // Reset the conversation history
      setConversationHistory([]);
      setSimulationStep(0);
      reset();

      // Generate auto-greeting if enabled
      const autoGreeting = await generateAutomaticGreeting();

      if (autoGreeting) {
        // Insert script item
        insertScriptItem(autoGreeting, ["welcome", "greeting"], {
          confidence: 1.0,
          intent: "greeting",
        });

        // Auto-send the greeting if we're using mock mode
        if (useAutoGreeting) {
          // Add to conversation history
          setConversationHistory([
            {
              speaker: "sdr",
              text: autoGreeting,
            },
          ]);

          // Send as transcript update
          sendTranscriptUpdate("host", autoGreeting);

          // Auto-generate customer response
          setTimeout(() => {
            handleAutoCustomerResponse();
          }, 1000);

          return;
        }
      }

      // Send a special "initialization" message to the FeedbackServer
      // This leverages the custom prompt functionality we added to specify host/company
      botSocket.send(
        JSON.stringify({
          type: "INITIALIZE_FEEDBACK",
          payload: {
            hostName: sdrName,
            companyName: companyName,
            customerPersona: personas[persona],
            needsIntroCard: true,
          },
          timestamp: new Date().toISOString(),
        }),
      );

      // Simulate a customer greeting to start the conversation
      const selectedPersona = personas[persona as keyof typeof personas];
      const customerGreeting = `Hi, I'm ${selectedPersona.name} from ${selectedPersona.company}. I received your email and wanted to learn more about your solution.`;

      // Add to conversation history
      setConversationHistory([
        {
          speaker: "customer",
          text: customerGreeting,
        },
      ]);

      // Send as transcript update
      sendTranscriptUpdate("guest", customerGreeting);

      // After sending the customer greeting, the Live Guidance feature should automatically
      // generate a script suggestion with the proper greeting format
    } catch (error) {
      console.error("Error triggering initial greeting:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Send transcript update to the bot socket
  const sendTranscriptUpdate = (speaker: "host" | "guest", text: string) => {
    const time = Date.now() / 1000;

    // Send to the actual bot socket
    botSocket.send(
      JSON.stringify({
        type: "SIMULATE_TRANSCRIPT",
        payload: { mode: speaker, text, time },
        timestamp: new Date().toISOString(),
      }),
    );

    // Send to API for realtime processing
    botSocket.send(
      JSON.stringify({
        type: "TRANSCRIPT_UPDATE",
        payload: {
          speaker: speaker === "host" ? "host" : "guest",
          text,
          timestamp: time,
        },
        timestamp: new Date().toISOString(),
      }),
    );

    // Add to our internal conversation history
    setConversationHistory((prev) => [
      ...prev,
      {
        speaker: speaker === "host" ? "sdr" : "customer",
        text,
      },
    ]);

    // Create transcript event for local script store
    const transcriptEvent: TranscriptEvent = {
      id: Date.now().toString(),
      timestamp: Date.now(),
      speaker: speaker === "host" ? "sdr" : "customer",
      text,
    };

    // Add to script store
    addTranscriptEvent(transcriptEvent);

    // Update simulation step
    setSimulationStep((prev) => prev + 1);
  };

  // Handle sending the SDR's response (using the script suggestion)
  const handleSendSDRResponse = (scriptText: string) => {
    if (!scriptText) return;

    // Send as host
    sendTranscriptUpdate("host", scriptText);

    // After sending SDR response, automatically generate a customer response
    handleAutoCustomerResponse();
  };

  // Automatically generate a customer response based on conversation history
  const handleAutoCustomerResponse = async () => {
    setIsLoading(true);

    try {
      // Format the conversation history
      const historyText = conversationHistory
        .map(
          (item) =>
            `${
              item.speaker === "sdr"
                ? sdrName
                : personas[persona as keyof typeof personas].name
            }: ${item.text}`,
        )
        .join("\n");

      // Get customer response
      const response = await getCustomerResponse(persona, historyText);

      // Send the response
      sendTranscriptUpdate("guest", response);

      // If simulation is running, handle next step
      if (simulationRunning && simulationStep < maxSimulationSteps) {
        setTimeout(() => {
          if (currentItem) {
            handleSendSDRResponse(currentItem.text);
          }
        }, 2000);
      } else if (simulationStep >= maxSimulationSteps) {
        setSimulationRunning(false);
      }
    } catch (error) {
      console.error("Error generating automatic customer response:", error);
      setSimulationRunning(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle sending a scripted conversation turn
  const handleScriptedMessage = () => {
    if (transcriptIndex >= sampleDialogue.length) {
      setTranscriptIndex(0);
      return;
    }

    const message = sampleDialogue[transcriptIndex];
    sendTranscriptUpdate(
      message.speaker === "sdr" ? "host" : "guest",
      message.text,
    );

    setTranscriptIndex(transcriptIndex + 1);
  };

  // Handle generating a manual LLM-based customer response
  const handleGenerateResponse = async () => {
    // Format the conversation history
    const historyText = conversationHistory
      .map(
        (item) =>
          `${
            item.speaker === "sdr"
              ? sdrName
              : personas[persona as keyof typeof personas].name
          }: ${item.text}`,
      )
      .join("\n");

    const response = await getCustomerResponse(persona, historyText);
    setCustomerResponse(response);
  };

  // Send the generated customer response
  const handleSendCustomerResponse = () => {
    if (!customerResponse) return;

    sendTranscriptUpdate("guest", customerResponse);
    setCustomerResponse("");
  };

  // Run fully automated simulation
  const runFullSimulation = async () => {
    if (simulationRunning) {
      setSimulationRunning(false);
      return;
    }

    // Reset and start new
    reset();
    setConversationHistory([]);
    setSimulationStep(0);
    setSimulationRunning(true);

    try {
      // Create conversation config
      const config: ConversationConfig = {
        sdr: {
          name: sdrName,
          role: "SDR",
          company: companyName,
        },
        customer: {
          name: personas[persona as keyof typeof personas].name,
          role: personas[persona as keyof typeof personas].role,
          company: personas[persona as keyof typeof personas].company,
        },
        greetingTemplate: `Hello, I'm {name} from {company}. I'm reaching out today to learn more about your current challenges and see if we might be able to help. Could you tell me a bit about what brought you to this call?`,
        strategyType: "bant",
      };

      // Run the simulation using the strategy
      const conversation = await bantStrategy.runSimulation(
        config,
        maxSimulationSteps,
      );

      // Process each event in sequence
      for (const event of conversation) {
        // Skip system messages
        if (event.speaker === "system") continue;

        // Add to conversation history
        setConversationHistory((prev) => [
          ...prev,
          {
            speaker: event.speaker,
            text: event.text,
          },
        ]);

        // Send to bot socket
        sendTranscriptUpdate(
          event.speaker === "sdr" ? "host" : "guest",
          event.text,
        );

        // Increment simulation step
        setSimulationStep((prev) => prev + 1);

        // Add delay between messages
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    } catch (error) {
      console.error("Error running simulation:", error);
    } finally {
      setSimulationRunning(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h3 className="text-sm font-medium">Call Setup</h3>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label htmlFor="your-name" className="text-xs text-gray-700">
              Your Name
            </label>
            <input
              id="your-name"
              type="text"
              value={sdrName}
              onChange={(e) => setSdrName(e.target.value)}
              className="w-full rounded border px-2 py-1 text-sm"
            />
          </div>
          <div>
            <label htmlFor="your-company" className="text-xs text-gray-700">
              Your Company
            </label>
            <input
              id="your-name"
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full rounded border px-2 py-1 text-sm"
            />
          </div>
        </div>

        <div>
          <label htmlFor="customer-type" className="text-xs text-gray-700">
            Customer Type
          </label>
          <Select
            value={persona}
            onValueChange={(
              value: "executive" | "technical" | "skeptical" | "enthusiastic",
            ) => setPersona(value)}
          >
            <SelectTrigger className="text-sm">
              <SelectValue placeholder="Select customer persona" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="executive">Executive Buyer</SelectItem>
              <SelectItem value="technical">Technical Evaluator</SelectItem>
              <SelectItem value="skeptical">Skeptical Prospect</SelectItem>
              <SelectItem value="enthusiastic">Enthusiastic Starter</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button
            size="sm"
            variant="default"
            onClick={triggerInitialGreeting}
            disabled={isLoading || simulationRunning}
            className="w-full"
          >
            {isLoading ? "Starting..." : "Start New Call"}
          </Button>

          <Button
            size="sm"
            variant={simulationRunning ? "destructive" : "outline"}
            onClick={runFullSimulation}
            disabled={isLoading}
            className="w-full"
          >
            {simulationRunning ? "Stop Simulation" : "Auto Simulate"}
          </Button>
        </div>

        {simulationRunning && (
          <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="h-full bg-blue-500 transition-all"
              style={{
                width: `${(simulationStep / maxSimulationSteps) * 100}%`,
              }}
            />
          </div>
        )}
      </div>

      {conversationHistory.length > 0 && (
        <div className="space-y-2 border-t pt-4">
          <h3 className="text-sm font-medium">Conversation History</h3>
          <ScrollArea className="max-h-[150px] space-y-2 overflow-y-auto rounded border p-2">
            {conversationHistory.map((item, index) => (
              <div
                key={`item-${index}`}
                className={`rounded p-1.5 text-sm ${
                  item.speaker === "sdr" ? "ml-4 bg-blue-50" : "mr-4 bg-gray-50"
                }`}
              >
                <div className="text-xs font-medium">
                  {item.speaker === "sdr"
                    ? sdrName
                    : personas[persona as keyof typeof personas].name}
                </div>
                <div>{item.text}</div>
              </div>
            ))}
            {isLoading && (
              <div className="animate-pulse py-1 text-center text-xs text-gray-500">
                Generating response...
              </div>
            )}
          </ScrollArea>

          <div className="text-center text-xs text-gray-500">
            {simulationRunning
              ? "Simulation is running automatically"
              : "The suggested script appears in the Live Guidance panel. You should read it aloud."}
          </div>

          {!simulationRunning && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                // Here we'd normally get the script suggestion from the LiveGuidance component
                // For now, we'll use a simple prompt to ask the user to look at the suggestion
                if (currentItem) {
                  handleSendSDRResponse(currentItem.text);
                  completeScriptItem();
                } else {
                  const scriptText = window.prompt(
                    "What does the script suggestion say? (Copy and paste it here)",
                  );
                  if (scriptText) {
                    handleSendSDRResponse(scriptText);
                  }
                }
              }}
              className="w-full"
              disabled={isLoading}
            >
              {currentItem
                ? "Use Current Script Suggestion"
                : "I've Read the Script Suggestion"}
            </Button>
          )}
        </div>
      )}

      <div className="space-y-2 border-t pt-4">
        <h3 className="text-sm font-medium">Advanced Options</h3>
        <div className="grid grid-cols-2 gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleScriptedMessage}
            disabled={simulationRunning}
          >
            Send Scripted Line
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setTranscriptIndex(0)}
            disabled={simulationRunning}
          >
            Reset Script
          </Button>
        </div>

        <div className="space-y-1">
          <Button
            size="sm"
            variant="outline"
            onClick={handleGenerateResponse}
            disabled={
              isLoading || conversationHistory.length === 0 || simulationRunning
            }
            className="w-full"
          >
            Generate Custom Response
          </Button>

          {customerResponse && !simulationRunning && (
            <div className="space-y-2">
              <Textarea
                value={customerResponse}
                onChange={(e) => setCustomerResponse(e.target.value)}
                rows={2}
                className="resize-none text-sm"
              />
              <Button
                size="sm"
                variant="default"
                onClick={handleSendCustomerResponse}
                className="w-full"
                disabled={simulationRunning}
              >
                Send Custom Response
              </Button>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-2">
          <label htmlFor="auto-greeting" className="text-xs text-gray-700">
            Use Auto-Greeting System
          </label>
          <div className="relative inline-block h-5 w-10 select-none align-middle transition duration-200 ease-in">
            <input
              type="checkbox"
              name="auto-greeting"
              id="auto-greeting"
              checked={useAutoGreeting}
              onChange={(e) => setUseAutoGreeting(e.target.checked)}
              className="toggle-checkbox absolute block h-6 w-6 cursor-pointer appearance-none rounded-full border-4 bg-white"
            />
            <span
              className={`toggle-label block h-6 cursor-pointer overflow-hidden rounded-full ${
                useAutoGreeting ? "bg-blue-500" : "bg-gray-300"
              }`}
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
